<template>
  <v-container class="px-1 py-1" fluid data-testid="collection-card">
    <v-row>
      <v-col cols="12" class="py-0">
        <v-card class="pl-3 pr-4 py-2" elevation="0" outlined>
          <v-row>
            <v-col cols="12" class="d-flex justify-space-between py-0 pr-2">
              <router-link :to="'/collections/'+collection.id" class="flex-grow-1">
                <div class="d-flex flex-column">
                  <h4 data-testid="collection-card-name" class="mb-0 primary--text font-weight-bold" v-bind:class="{'text--darken-3': !$vuetify.theme.dark,'text--lighten-3': $vuetify.theme.dark}">
                        {{collection.name}}
                      </h4>
                  <p data-testid="collection-card-creator" v-if="collection.createdBy" class="body-2 primary--text subheader-font mb-0 mt-0">{{ `${$t("general.createdBy")} ${collection.createdByName || collection.createdBy}`}} <span v-if="collection.supervised">({{collection.userType}})</span></p>
                </div>
              </router-link>
              <div class="d-flex align-center justify-end">
                <!-- Players Collected Badge -->
                <div v-if="embedded" class="d-flex align-center">
                  <div data-testid="collection-card-player-count" class="primary white--text px-3 py-2 rounded monospace-font font-weight-bold d-flex align-center justify-center" style="min-width: 50px; height: 100%;">
                    {{nPlayers}}
                  </div>
                </div>
                <!--Pin-->
                <v-btn data-testid="collection-card-pin-button" icon x-small @click="onPinClick()" 
                  v-if="(collection.createdBy === currentUser.username || userHasRole('ROLE_ADMIN')) && !embedded"
                ><v-icon small :color="collection.pinned ? 'secondary' : 'grey lighten-1 z-10'" style="font-size: 12px;">fa-thumbtack</v-icon>
                </v-btn>
                <!--Delete-->
                <v-btn data-testid="collection-card-delete-button" icon x-small @click="onDeleteClick()" 
                  v-if="(collection.createdBy === currentUser.username || userHasRole('ROLE_ADMIN')) && !embedded"
                ><v-icon small color="grey lighten-1 z-10">delete</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
          <v-divider v-if="embedded" class="my-2"></v-divider>
          <router-link :to="'/collections/'+collection.id">
            <v-row>
              <v-col cols="6" class="pb-1 py-1" v-if="!$vuetify.breakpoint.smAndDown && !embedded">
                <v-row>
                  <v-col cols="12" class="d-flex align-end justify-space-between py-0">
                    <span class="body-2 ml-2 orange--text text--darken-3">{{$t('positions.goalkeepers')}}</span>
                    <span class="body-2 font-weight-medium primary--text d-flex justify-end mx-2">{{ goalKeepers }}</span>
                  </v-col>
                  <v-col cols="12" class="d-flex align-end justify-space-between py-0">
                    <span class="body-2 ml-2 indigo--text text--darken-1">{{$t('positions.defenses')}}</span>
                    <span class="body-2 font-weight-medium primary--text d-flex justify-end mx-2">{{ defenders }}</span>
                  </v-col>
                  <v-col cols="12" class="d-flex align-end justify-space-between py-0">
                    <span class="body-2 ml-2 success--text text--darken-2">{{$t('positions.midfielders')}}</span>
                    <span class="body-2 font-weight-medium primary--text d-flex justify-end mx-2">{{ midfielders }}</span>
                  </v-col>
                  <v-col cols="12" class="d-flex align-end justify-space-between py-0">
                    <span class="body-2 ml-2 red--text text--darken-3">{{$t('positions.forwards')}}</span>
                    <span class="body-2 font-weight-medium primary--text d-flex justify-end mx-2">{{ forwards }}</span>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" :sm="embedded ? 12 : 6" class="pb-1 pt-0">
                <v-row>
                  <v-col cols="12" class="d-flex align-end justify-space-between py-1">
                    <span class="body-2 primary--text subheader-font">{{$t('collections.totalMarketValue')}}</span>
                    <span class="body-2 font-weight-medium primary--text">{{totalMarketValue | formatCash(currentUserRate)}}</span>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="d-flex align-end justify-space-between py-1">
                    <span class="body-2 primary--text subheader-font">{{$t('collections.avgMarketValue')}}</span>
                    <span class="body-2 font-weight-medium primary--text">{{avgMarketValue | formatCash(currentUserRate)}}</span>
                  </v-col>
                </v-row>
                <v-row v-if="embedded && uniquePositions.length > 0">
                  <v-col cols="12" class="d-flex align-end justify-space-between py-1">
                    <span class="body-2 primary--text subheader-font">{{$t('playerRecruitment.positions')}}</span>
                    <div class="d-flex align-center">
                      <v-chip
                        v-for="(posId, index) in displayedPositions"
                        :key="index"
                        x-small
                        :color="getPositionChipColor(posId)"
                        text-color="white"
                        class="ml-1 monospace-font"
                      >
                        {{getPositionCode(posId)}}
                      </v-chip>
                      <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="hiddenPositionsCount > 0">
                        <template v-slot:activator="{ on, attrs }">
                          <v-chip
                            v-on="on"
                            v-bind="attrs"
                            x-small
                            color="grey darken-1"
                            text-color="white"
                            class="ml-1 monospace-font"
                          >
                            +{{hiddenPositionsCount}}
                          </v-chip>
                        </template>
                        <div class="d-flex flex-wrap align-center">
                          <v-chip
                            v-for="(posId, index) in hiddenPositions"
                            :key="index"
                            x-small
                            :color="getPositionChipColor(posId)"
                            text-color="white"
                            class="ma-1 monospace-font"
                          >
                            {{getPositionCode(posId)}}
                          </v-chip>
                        </div>
                      </v-tooltip>
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" class="d-flex align-end justify-space-between py-1">
                    <span class="body-2 primary--text subheader-font">{{$t('collections.avgAge')}}</span>
                    <span class="body-2 font-weight-medium primary--text">{{ avgAge }}yo</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </router-link>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>

import CollectionService from '../../services/collection.service';
import * as _ from 'lodash';
import {formatCash,isForward,isMidfielder,isDefender,isGoalkeeper,getCurrencies,userHasRole,getPositionCode,getChipColorByPosition} from '../../utils/functions'

export default {
  name: 'CollectionCard',
  components: {
  },
  props:{
    collection:{
      type: Object,
      default: false,
    },
    embedded:{
      type: Boolean,
      default: false,
    }
  },
  computed:{
    nPlayers(){
      return _.size(_.get(this.collection,'players',[])) || '0';
    },
    totalMarketValue(){
      return _.reduce(this.collection.players,(sum,p) => {
        sum += _.get(p,'marketValue',0)
        return sum;
      },0) * 1000
    },
    avgMarketValue(){
      if(_.size(this.collection.players)){
        return (_.reduce(this.collection.players,(sum,p) => {
          sum += _.get(p,'marketValue',0)
          return sum;
        },0) * 1000) / _.size(this.collection.players)
      }
      return 0;
    },
    avgAge(){
      if(_.size(this.collection.players)){
        return _.floor(_.reduce(this.collection.players,(sum,p) => {
          sum += _.get(p,'age',0)
          return sum;
        },0) / _.size(this.collection.players))
      }
      return 0;
    },
    forwards(){
      return _.reduce(this.collection.players,(sum,p) => {
        if(isForward(p.fieldPositionId)){
          sum++
        }
        return sum;
      },0)
    },
    midfielders(){
      return _.reduce(this.collection.players,(sum,p) => {
        if(isMidfielder(p.fieldPositionId)){
          sum++
        }
        return sum;
      },0)
    },
    defenders(){
      return _.reduce(this.collection.players,(sum,p) => {
        if(isDefender(p.fieldPositionId)){
          sum++
        }
        return sum;
      },0)
    },
    goalKeepers(){
      return _.reduce(this.collection.players,(sum,p) => {
        if(isGoalkeeper(p.fieldPositionId)){
          sum++
        }
        return sum;
      },0)
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentUserCurrency(){
      return this.$store.getters["auth/currency"];
    },
    currentUserRate(){
      let rate = this.currentUserCurrency === "EUR" ? 1 : this.$store.getters["exchange/rates"][this.$store.getters["auth/currency"]];
      return rate ? rate : 1;
    },
    currencies(){
      return getCurrencies();
    },
    currentUserCurrencySymbol(){
      let currentCurrencyObj = _.find(this.currencies,(c) => c.code === this.currentUserCurrency)
      return currentCurrencyObj.symbol;
    },
    uniquePositions(){
      if(!this.collection.players || !_.size(this.collection.players)) return [];
      const positions = new Set();
      this.collection.players.forEach(p => {
        if(p.fieldPositionId) positions.add(p.fieldPositionId);
        if(p.secondFieldPositionId) positions.add(p.secondFieldPositionId);
      });
      return Array.from(positions).sort();
    },
    displayedPositions(){
      return this.uniquePositions.slice(0, 3);
    },
    hiddenPositions(){
      return this.uniquePositions.slice(3);
    },
    hiddenPositionsCount(){
      return Math.max(0, this.uniquePositions.length - 3);
    }
  },
  filters: {
    formatCash(n,rate) {
      return formatCash(n * rate)
    }
  },
  data() {
    return {
    };
  },
  mounted(){
    if(!this.collection.players || !_.size(this.collection.players)){
      this.loadPlayers();
    }
  },
  methods: {
    onDeleteClick(){
      this.$emit('deleteClicked',true)
    },
    userHasRole(role){
      return userHasRole(this.currentUser,role);
    },
    async loadPlayers(){
      let collectionResponse =  await CollectionService.getCollectionById(this.collection.id);
      this.collection.players = collectionResponse.data.players;
    },
    onPinClick(){
      this.$emit('pinClicked', true);
    },
    getPositionCode(positionId){
      return getPositionCode(this.currentUser.fieldPositionCodeId, positionId);
    },
    getPositionChipColor(positionId){
      return getChipColorByPosition(positionId);
    },
  }
};
</script>
